import { FC, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { TablePagination } from "components/paginations/TablePagination"
import { Column, DataTable } from "components/tables/DataTable"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CMS_LOCALE_NAMES, CMS_LOCALES, CmsPageListItem, CmsPageStatus, CmsPageType } from "services/cms-seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { AsyncList, dangerButtonClass, FilterField, IndexFlag, InfoBox, inputClass, LocaleChips, PathText, primaryButtonClass, ScoreChip, StatusBadge, TypeBadge } from "../CmsUi"
import { formatDateTime } from "../helpers/format"
import { usePageList } from "../helpers/usePageList"

const PAGE_SIZES = [10, 25, 50]

interface PagesTabProps {
  onOpen: (id: string) => void
  onCreate: () => void
}

export const PagesTab: FC<PagesTabProps> = ({ onOpen, onCreate }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const site = useBrandSite()
  const [locale, setLocale] = useState<string>("en")
  const [status, setStatus] = useState<CmsPageStatus | "">("")
  const [type, setType] = useState<CmsPageType | "">("")
  const [search, setSearch] = useState("")
  const [deleteCandidate, setDeleteCandidate] = useState<CmsPageListItem | null>(null)

  const filters = useMemo(
    () => ({
      locale,
      status: status || undefined,
      type: type || undefined,
      q: search.trim() || undefined
    }),
    [locale, status, type, search]
  )

  const list = usePageList(filters)

  /** Opens the new language straight away — the point of the click was to edit it. */
  const createTranslation = async (row: CmsPageListItem, locale: string) => {
    const copy = await cmsSeoService.duplicatePage(row.id, { to_locale: locale })
    if (!copy) return

    toast.success(
      t("cmsSeo.translationCreated", {
        defaultValue: "{{name}} version created as a draft",
        name: CMS_LOCALE_NAMES[locale] ?? locale
      }) as string
    )
    list.refresh()
    onOpen(copy.id)
  }

  const confirmDelete = async (row: CmsPageListItem) => {
    if (await cmsSeoService.deletePage(row.id)) {
      toast.success(t("cmsSeo.pageDeleted", { defaultValue: "Page deleted" }) as string)
      list.refresh()
    }
  }

  const columns: Column<CmsPageListItem>[] = [
    {
      header: t("cmsSeo.col.path", { defaultValue: "Path" }),
      render: (row) => <PathText>{row.path}</PathText>
    },
    {
      header: t("cmsSeo.col.title", { defaultValue: "Title" }),
      wrap: true,
      render: (row) => <span className="text-sm">{row.title}</span>
    },
    {
      header: t("cmsSeo.col.type", { defaultValue: "Type" }),
      hideBelow: "lg",
      render: (row) => <TypeBadge type={row.type} />
    },
    {
      header: t("cmsSeo.col.status", { defaultValue: "Status" }),
      render: (row) => <StatusBadge status={row.status} />
    },
    {
      header: t("cmsSeo.col.index", { defaultValue: "Index" }),
      hideBelow: "md",
      render: (row) => <IndexFlag indexed={row.robots_index} />
    },
    {
      header: t("cmsSeo.col.seo", { defaultValue: "SEO" }),
      hideBelow: "sm",
      render: (row) => <ScoreChip passed={row.seo_score.passed} total={row.seo_score.total} />
    },
    {
      header: t("cmsSeo.col.locales", { defaultValue: "Locales" }),
      hideBelow: "lg",
      render: (row) => (
        <LocaleChips
          locales={row.locales}
          missing={row.missing_locales}
          onCreate={(locale) => createTranslation(row, locale)}
        />
      )
    },
    {
      header: t("cmsSeo.col.updated", { defaultValue: "Updated" }),
      hideBelow: "xl",
      render: (row) => (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatDateTime(row.updated_at)}
          {row.updated_by ? ` · ${row.updated_by}` : ""}
        </span>
      )
    },
    {
      header: t("common.actions", { defaultValue: "Actions" }),
      align: "right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className={primaryButtonClass}
            onClick={(event) => {
              event.stopPropagation()
              onOpen(row.id)
            }}
          >
            {t("common.edit", { defaultValue: "Edit" })}
          </button>
          <button
            type="button"
            className={dangerButtonClass}
            onClick={(event) => {
              event.stopPropagation()
              setDeleteCandidate(row)
            }}
          >
            {t("common.delete", { defaultValue: "Delete" })}
          </button>
        </div>
      )
    }
  ]

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      {site.localePrefix === "as-needed" && (
        <InfoBox>
          {t("cmsSeo.pagesRootLocaleInfo", {
            defaultValue:
              "This brand serves {{language}} from the root of the domain: a {{language}} page is published at {{host}}/promotions, while the same page in another language keeps its code — {{host}}/pt/promotions. Each language still has its own title, description and content, all of them appear in the sitemap, and the tags telling search engines these are translations of one another are generated for both shapes. One rule follows from it: a top-level page cannot use a language code as its address, because that address already belongs to a language.",
            language: site.defaultLocale.toUpperCase(),
            host: site.host || "yourdomain.com"
          })}
        </InfoBox>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <FilterField label={t("cmsSeo.filter.locale", { defaultValue: "Locale" })}>
          <select className={inputClass} value={locale} onChange={(event) => setLocale(event.target.value)}>
            {CMS_LOCALES.map((code) => (
              <option key={code} value={code}>
                {CMS_LOCALE_NAMES[code]} ({code.toUpperCase()})
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label={t("cmsSeo.filter.status", { defaultValue: "Status" })}>
          <select className={inputClass} value={status} onChange={(event) => setStatus(event.target.value as CmsPageStatus | "")}>
            <option value="">{t("common.all", { defaultValue: "All" })}</option>
            <option value="published">{t("cmsSeo.status.published", { defaultValue: "Published" })}</option>
            <option value="draft">{t("cmsSeo.status.draft", { defaultValue: "Draft" })}</option>
            <option value="scheduled">{t("cmsSeo.status.scheduled", { defaultValue: "Scheduled" })}</option>
          </select>
        </FilterField>

        <FilterField label={t("cmsSeo.filter.type", { defaultValue: "Type" })}>
          <select className={inputClass} value={type} onChange={(event) => setType(event.target.value as CmsPageType | "")}>
            <option value="">{t("common.all", { defaultValue: "All" })}</option>
            <option value="page">{t("cmsSeo.type.page", { defaultValue: "Page" })}</option>
            <option value="article">{t("cmsSeo.type.article", { defaultValue: "Article" })}</option>
            <option value="landing">{t("cmsSeo.type.landing", { defaultValue: "Landing" })}</option>
          </select>
        </FilterField>

        <FilterField label={t("common.search", { defaultValue: "Search" })} grow>
          <input
            className={inputClass}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("cmsSeo.filter.searchPages", { defaultValue: "Search by title, path or slug…" }) as string}
          />
        </FilterField>

        <button type="button" className={`${primaryButtonClass} w-full sm:w-auto`} onClick={onCreate}>
          {t("cmsSeo.createPage", { defaultValue: "+ Create page" })}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <h2 className="flex-1 text-base font-semibold text-black dark:text-white">{t("cmsSeo.tab.pages", { defaultValue: "Pages" })}</h2>
        <select className={`${inputClass} w-auto`} value={list.pageSize} onChange={(event) => list.setPageSize(Number(event.target.value))}>
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {t("cmsSeo.rowsPerPage", { defaultValue: "Rows: {{count}}", count: size })}
            </option>
          ))}
        </select>
      </div>

      <AsyncList
        loading={list.loading}
        isEmpty={list.rows.length === 0}
        emptyText={t("cmsSeo.noPages", { defaultValue: "No pages match these filters." }) as string}
      >
        <DataTable rows={list.rows} rowKey={(row) => row.id} columns={columns} onRowClick={(row) => onOpen(row.id)} />
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span>{t("cmsSeo.showingCount", { defaultValue: "{{count}} pages", count: list.total })}</span>
          <TablePagination page={list.page} totalPages={Math.max(list.totalPages, 1)} onJump={list.setPage} />
        </div>
      </AsyncList>

      <ModalConfirmAction
        data={deleteCandidate}
        handler={() => setDeleteCandidate(null)}
        text={
          deleteCandidate
            ? (t("cmsSeo.confirmDeletePage", {
                defaultValue: 'Delete "{{title}}" ({{path}})? A 301 redirect is not created automatically.',
                title: deleteCandidate.title,
                path: deleteCandidate.path
              }) as string)
            : ""
        }
        actionText={t("common.delete", { defaultValue: "Delete" })}
        actionClassName="btn-error"
        actionHandler={confirmDelete}
      />
    </div>
  )
}
